import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, User } from '../lib/api';
import { isServerUnavailableError } from '../lib/api/http';
import { useSettingsStore } from './settings';

/** Server startet neu oder Netz weg: so oft im Hintergrund nachfragen, bis er wieder antwortet. */
export const AUTH_RECHECK_MS = 15_000;

let recheckTimer: ReturnType<typeof setTimeout> | null = null;

function stopRecheck() {
  if (recheckTimer) clearTimeout(recheckTimer);
  recheckTimer = null;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  /** Nur lokal: z. B. bei 401 ohne Logout-API (Sitzung serverseitig ungültig). */
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const signOutLocally = () => {
        stopRecheck();
        useSettingsStore.getState().setHasLlmKey(false);
        set({ user: null, isAuthenticated: false, isLoading: false });
      };

      /**
       * Nachfragen, ohne isLoading anzufassen: PrivateRoute ersetzt die Seite sonst durch den
       * Lader — ein offener Bericht würde ausgehängt.
       */
      const recheck = async () => {
        recheckTimer = null;
        try {
          const { user } = await api.getMe();
          set({ user, isAuthenticated: true });
          void useSettingsStore.getState().loadLlmKeyStatus();
        } catch (error) {
          if (isServerUnavailableError(error)) {
            recheckTimer = setTimeout(() => void recheck(), AUTH_RECHECK_MS);
            return;
          }
          signOutLocally();
        }
      };

      return {
        user: null,
        isLoading: true,
        isAuthenticated: false,

        logout: async () => {
          stopRecheck();
          try {
            await api.logout();
          } catch {
            // Ignore logout errors
          }
          useSettingsStore.getState().setHasLlmKey(false);
          set({ user: null, isAuthenticated: false, isLoading: false });
        },

        checkAuth: async () => {
          stopRecheck();
          set({ isLoading: true });
          try {
            const { user } = await api.getMe();
            set({ user, isAuthenticated: true, isLoading: false });
            void useSettingsStore.getState().loadLlmKeyStatus();
          } catch (error) {
            // Ein Server, der gerade neu startet (Deploy) oder nicht erreichbar ist, sagt nichts
            // über die Anmeldung. Bis v1.354.2 landete jeder, der in diesem Moment neu lud, auf der
            // Anmeldeseite. Abgemeldet wird, wenn der Server die Sitzung ablehnt (401 & Co.).
            const knownUser = get().user;
            if (knownUser && isServerUnavailableError(error)) {
              set({ user: knownUser, isAuthenticated: true, isLoading: false });
              recheckTimer = setTimeout(() => void recheck(), AUTH_RECHECK_MS);
              return;
            }
            signOutLocally();
          }
        },

        setUser: (user: User | null) => {
          set({ user, isAuthenticated: !!user });
        },

        clearSession: () => {
          signOutLocally();
        },
      };
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
